/**
 * Version Check Composable
 * Automatically detects new deployments and prompts users to refresh
 */

import { ref, onMounted, onUnmounted } from 'vue';

interface VersionInfo {
  version: string;
  buildTime: string;
  buildTimestamp: number;
  gitCommit: string;
  gitBranch: string;
  environment: string;
  buildId: string;
}

interface UseVersionCheckOptions {
  /** Check interval in milliseconds (default: 60000 = 1 minute) */
  checkInterval?: number;
  /** Enable in development mode (default: false) */
  enableInDev?: boolean;
}

const currentVersion = ref<VersionInfo | null>(null);
const hasUpdate = ref(false);
const isChecking = ref(false);
const checkError = ref<string | null>(null);

export function useVersionCheck(options: UseVersionCheckOptions = {}) {
  const { 
    checkInterval = 60000, // 1 minute default
    enableInDev = false 
  } = options;

  let intervalId: ReturnType<typeof setInterval> | null = null;
  let visibilityHandler: (() => void) | null = null;

  /**
   * Fetch current version from server
   */
  async function fetchVersion(): Promise<VersionInfo | null> {
    try {
      // Add cache-busting query param
      const response = await fetch(`/version.json?v=${Date.now()}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.warn('[VersionCheck] Failed to fetch version:', error);
      return null;
    }
  }

  /**
   * Check if there's a new version available
   */
  async function checkForUpdate(_silent: boolean = false): Promise<boolean> {
    if (isChecking.value) return false;
    
    // Skip in development unless explicitly enabled
    if ((import.meta as any).env?.DEV && !enableInDev) {
      return false;
    }

    isChecking.value = true;
    checkError.value = null;

    try {
      const serverVersion = await fetchVersion();

      if (!serverVersion) {
        checkError.value = 'Failed to fetch version';
        return false;
      }

      // First check - store the version
      if (!currentVersion.value) {
        currentVersion.value = serverVersion;
        console.log('[VersionCheck] Initial version:', serverVersion.buildId);
        return false;
      }

      // Compare build IDs
      if (serverVersion.buildId !== currentVersion.value.buildId) {
        console.log('[VersionCheck] New version detected!');
        console.log('  Old:', currentVersion.value.buildId);
        console.log('  New:', serverVersion.buildId);
        
        hasUpdate.value = true;
        return true;
      }

      return false;
    } catch (error) {
      checkError.value = error instanceof Error ? error.message : 'Unknown error';
      return false;
    } finally {
      isChecking.value = false;
    }
  }

  /**
   * Refresh the page to load the new version
   */
  function refreshApp(): void {
    // Clear any cached data
    localStorage.removeItem('lms_cache_version');
    
    // Force reload from server (not cache)
    window.location.reload();
  }

  /**
   * Dismiss the update notification (check again later)
   */
  function dismissUpdate(): void {
    hasUpdate.value = false;
  }

  /**
   * Start periodic version checking
   */
  function startChecking(): void {
    // Check immediately on start
    checkForUpdate(true);

    // Set up interval
    intervalId = setInterval(() => {
      checkForUpdate(true);
    }, checkInterval);

    // Check when tab becomes visible (user returns to app)
    visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate(true);
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    console.log('[VersionCheck] Started checking every', checkInterval, 'ms');
  }

  /**
   * Stop periodic version checking
   */
  function stopChecking(): void {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
    }
    console.log('[VersionCheck] Stopped checking');
  }

  // Auto-start on mount (in production)
  onMounted(() => {
    if (!(import.meta as any).env?.DEV || enableInDev) {
      startChecking();
    }
  });

  // Clean up on unmount
  onUnmounted(() => {
    stopChecking();
  });

  return {
    // State
    currentVersion,
    hasUpdate,
    isChecking,
    checkError,
    
    // Methods
    checkForUpdate,
    refreshApp,
    dismissUpdate,
    startChecking,
    stopChecking
  };
}

export default useVersionCheck;
